import { useState, useRef, useEffect, useMemo } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Checkbox,
    MenuItem,
    TextField,
    CircularProgress,
    Alert,
    Divider,
    IconButton,
    Tooltip,
    useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSignOut } from 'react-auth-kit';
import Axios from 'axios';
import { getAuthHeader } from '../../utils/authToken';
import { useSnackbar } from 'notistack';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SendIcon from '@mui/icons-material/Send';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import BuildIcon from '@mui/icons-material/Build';
import SummarizeIcon from '@mui/icons-material/Summarize';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import BugReportIcon from '@mui/icons-material/BugReport';
import ClearIcon from '@mui/icons-material/Clear';
import { styled, alpha } from '@mui/material/styles';
import { tableCellClasses } from '@mui/material/TableCell';
import { tokens } from '../../theme';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.mode === 'dark' ? '#1e4976' : '#1976d2',
        color: theme.palette.common.white,
        fontWeight: 'bold',
        fontSize: 12,
    },
    [`&.${tableCellClasses.body}`]: { fontSize: 13 },
}));

const API = `${process.env.REACT_APP_URL}/api/v1/auto-reports/dev/session`;
const TRIGGER_LABEL = {
    midterm_report: 'Midterm',
    three_remaining_email: '3-Remaining',
    two_remaining_email: '2-Remaining',
};
const TRIGGER_COLOR = {
    midterm_report: 'primary',
    three_remaining_email: 'warning',
    two_remaining_email: 'info',
};
const STATUS_COLOR = {
    processing: 'warning',
    pending: 'default',
    pending_review: 'info',
    confirmed: 'success',
    email_sent: 'success',
    email_failed: 'error',
    failed: 'error',
    skipped: 'default',
    office_notified: 'info',
    expired_unconfirmed: 'default',
    permanently_failed: 'error',
    completed: 'success',
};

const DevAutoReportTesting = () => {
    const navigate = useNavigate();
    const signOut = useSignOut();
    const { enqueueSnackbar } = useSnackbar();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    // Session state
    const [sessionActive, setSessionActive] = useState(false);
    const [busyPhase, setBusyPhase] = useState(null); // which phase button is currently running

    // Phase 1: candidates + selections
    const [candidates, setCandidates] = useState([]);
    const [selections, setSelections] = useState({}); // key: `${enrollment_id}_${trigger_type}` → { include, force_fail }

    // Phase 2+: live rows
    const [rows, setRows] = useState([]);
    const prevRowsRef = useRef([]); // for diff highlight
    const [flashRowIds, setFlashRowIds] = useState(new Set());
    const pollIntervalRef = useRef(null);

    // Cleanup poll on unmount
    useEffect(() => {
        return () => {
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
            }
        };
    }, []);

    // Action log (top panel)
    const [log, setLog] = useState([]);

    const appendLog = (text, data = null) => {
        const ts = new Date().toLocaleTimeString('en-US', { hour12: false });
        setLog(prev => [...prev, { ts, text, data }]);
    };

    const handleError = (error, fallbackMsg) => {
        if (error.response?.status === 401) {
            signOut();
            navigate('/signin');
            return;
        }
        const msg = error.response?.data?.error || fallbackMsg;
        enqueueSnackbar(msg, { variant: 'error' });
        appendLog(`ERROR: ${msg}`);
    };

    // ─── API wrappers ─────────────────────────────────────────────────────

    const callPhase = async (label, method, path, body) => {
        setBusyPhase(label);
        try {
            const config = { headers: { Authorization: getAuthHeader() } };
            let res;
            if (method === 'GET') res = await Axios.get(`${API}${path}`, config);
            else if (method === 'POST') res = await Axios.post(`${API}${path}`, body || {}, config);
            else if (method === 'PATCH') res = await Axios.patch(`${API}${path}`, body || {}, config);
            appendLog(label, res.data);
            return res.data;
        } catch (error) {
            handleError(error, `${label} failed`);
            return null;
        } finally {
            setBusyPhase(null);
        }
    };

    const fetchRows = async () => {
        try {
            const res = await Axios.get(`${API}/rows`, { headers: { Authorization: getAuthHeader() } });
            // Diff highlight: which rows changed status or retry count since the last fetch?
            const prev = prevRowsRef.current;
            const changed = new Set();
            for (const next of res.data.rows) {
                const before = prev.find(r => r.id === next.id);
                if (!before) { changed.add(next.id); continue; }
                if (before.status !== next.status || before.daily_retry_count !== next.daily_retry_count) {
                    changed.add(next.id);
                }
            }
            prevRowsRef.current = res.data.rows;
            setRows(res.data.rows);
            if (changed.size > 0) {
                setFlashRowIds(changed);
                setTimeout(() => setFlashRowIds(new Set()), 2500);
            }
        } catch (error) {
            handleError(error, 'Failed to fetch dev rows');
        }
    };

    // ─── Phase actions ────────────────────────────────────────────────────

    const startSession = async () => {
        const res = await callPhase('Start Session (wipe existing dev_test data)', 'POST', '/start');
        if (res) {
            setSessionActive(true);
            setCandidates([]);
            setSelections({});
            setRows([]);
            prevRowsRef.current = [];
        }
    };

    const loadCandidates = async () => {
        const res = await callPhase('Load Candidates', 'GET', '/candidates');
        if (res && Array.isArray(res.candidates)) {
            setCandidates(res.candidates);
            const defaults = {};
            for (const c of res.candidates) {
                defaults[`${c.enrollment_id}_${c.trigger_type}`] = { include: true, force_fail: '' };
            }
            setSelections(defaults);
        }
    };

    const toggleInclude = (key) => {
        setSelections(prev => ({ ...prev, [key]: { ...prev[key], include: !prev[key]?.include } }));
    };

    const setForceFail = (key, value) => {
        setSelections(prev => ({ ...prev, [key]: { ...prev[key], force_fail: value === 'none' ? '' : value } }));
    };

    const runScan = async () => {
        const payload = Object.entries(selections)
            .filter(([, v]) => v.include)
            .map(([key, v]) => {
                // Look the candidate up by key instead of splitting it — the
                // trigger_type portion contains underscores so naive splitting
                // can't reconstruct the parts.
                const c = candidates.find(
                    x => `${x.enrollment_id}_${x.trigger_type}` === key
                );
                return {
                    enrollment_id: c?.enrollment_id,
                    trigger_type: c?.trigger_type,
                    force_fail: v.force_fail || undefined,
                };
            })
            .filter(s => s.enrollment_id && s.trigger_type);
        if (payload.length === 0) {
            enqueueSnackbar('No candidates selected', { variant: 'warning' });
            return;
        }
        const res = await callPhase(`Run Scan (${payload.length} selections)`, 'POST', '/scan', { selections: payload });
        if (res) {
            // Content generation is async (midterm 5–15 min, 3-rem a few seconds).
            // Poll for 30s to pick up new rows as they transition PROCESSING → PENDING_REVIEW.
            // Interval is stored on a ref so unmount and repeat clicks don't leak timers.
            await fetchRows();
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
            }
            let tries = 6;
            pollIntervalRef.current = setInterval(async () => {
                await fetchRows();
                if (--tries <= 0) {
                    clearInterval(pollIntervalRef.current);
                    pollIntervalRef.current = null;
                }
            }, 5000);
        }
    };

    const sendPreview = async () => {
        const res = await callPhase('Send Preview (2-rem notification + midterm/3-rem summary)', 'POST', '/send-preview');
        if (res) fetchRows();
    };

    const runSends = async (target = 'all') => {
        const res = await callPhase(`Run Send (${target})`, 'POST', '/run-sends', { target });
        if (res) {
            // Per-row send workers are async too
            setTimeout(fetchRows, 2000);
            setTimeout(fetchRows, 6000);
        }
    };

    const runMaintenance = async () => {
        const res = await callPhase('Run Maintenance', 'POST', '/run-maintenance');
        if (res) fetchRows();
    };

    const runDigest = async () => {
        await callPhase('Run Daily Digest (to DEV_EMAIL)', 'POST', '/run-digest');
    };

    const cleanup = async () => {
        const res = await callPhase('Cleanup Session (delete all dev_test data)', 'POST', '/cleanup');
        if (res) {
            setSessionActive(false);
            setCandidates([]);
            setSelections({});
            setRows([]);
            prevRowsRef.current = [];
        }
    };

    const toggleRowForceFail = async (rowId, currentForceFail) => {
        const nextValue = currentForceFail === 'email_send' ? null : 'email_send';
        try {
            await Axios.patch(
                `${API}/rows/${rowId}/force-fail`,
                { force_fail: nextValue },
                { headers: { Authorization: getAuthHeader() } }
            );
            appendLog(`Row ${rowId}: force_fail = ${nextValue ?? '—'}`);
            fetchRows();
        } catch (error) {
            handleError(error, 'Failed to update force_fail');
        }
    };

    // ─── Derived state ────────────────────────────────────────────────────

    const includedCount = useMemo(
        () => Object.values(selections).filter(v => v.include).length,
        [selections]
    );

    const selectAllIncluded = (include) => {
        setSelections(prev => {
            const next = { ...prev };
            for (const key of Object.keys(next)) next[key] = { ...next[key], include };
            return next;
        });
    };

    // ─── Render ───────────────────────────────────────────────────────────

    return (
        <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
                <BugReportIcon />
                <Typography variant="h5" fontWeight="bold">Auto Reports — Dev Testing Simulator</Typography>
            </Box>
            <Alert severity="info" sx={{ mb: 2 }}>
                Simulates the full daily cron loop against <code>dev_test</code>-tagged rows. All emails are automatically
                redirected to <code>DEV_EMAIL</code> by the parent-portal queue when <code>NODE_ENV=development</code>.
                Only one session at a time — starting a new session wipes any existing dev_test data first.
            </Alert>

            {/* Action log */}
            <Paper sx={{ p: 2, mb: 2, maxHeight: 260, overflowY: 'auto', backgroundColor: colors.primary[600] }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography variant="subtitle2" fontWeight="bold">Action log</Typography>
                    <IconButton size="small" onClick={() => setLog([])}><ClearIcon fontSize="small" /></IconButton>
                </Box>
                {log.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">No actions yet. Click Start Session to begin.</Typography>
                ) : (
                    <Box component="pre" sx={{ m: 0, fontSize: 12, fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                        {log.map((entry, i) => (
                            <Box key={i}>
                                <strong>[{entry.ts}]</strong> {entry.text}
                                {entry.data && (
                                    <Box sx={{ pl: 2, color: 'text.secondary' }}>
                                        → {JSON.stringify(entry.data)}
                                    </Box>
                                )}
                            </Box>
                        ))}
                    </Box>
                )}
            </Paper>

            {/* Session controls */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <Button
                        variant="contained"
                        color={sessionActive ? 'inherit' : 'primary'}
                        startIcon={<RestartAltIcon />}
                        onClick={startSession}
                        disabled={busyPhase !== null}
                    >
                        {sessionActive ? 'Restart Session' : 'Start Session'}
                    </Button>
                    <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteForeverIcon />}
                        onClick={cleanup}
                        disabled={busyPhase !== null}
                    >
                        Cleanup Session
                    </Button>
                    <Box flexGrow={1} />
                    {busyPhase && (
                        <Box display="flex" alignItems="center" gap={1}>
                            <CircularProgress size={18} />
                            <Typography variant="caption">{busyPhase}</Typography>
                        </Box>
                    )}
                </Box>
            </Paper>

            {/* Phase 1: Setup */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography variant="h6" fontWeight="bold">Phase 1 — Setup</Typography>
                    <Box display="flex" gap={1}>
                        <Button
                            variant="outlined"
                            onClick={loadCandidates}
                            disabled={!sessionActive || busyPhase !== null}
                        >
                            Load Candidates
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<PlayArrowIcon />}
                            onClick={runScan}
                            disabled={!sessionActive || includedCount === 0 || busyPhase !== null}
                        >
                            Run Scan on {includedCount} Selected
                        </Button>
                    </Box>
                </Box>
                {candidates.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">
                        {sessionActive
                            ? 'Click Load Candidates to detect enrollments that would fire a trigger right now.'
                            : 'Start a session to begin.'}
                    </Typography>
                ) : (
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell padding="checkbox">
                                        <Checkbox
                                            size="small"
                                            checked={candidates.length > 0 && includedCount === candidates.length}
                                            indeterminate={includedCount > 0 && includedCount < candidates.length}
                                            onChange={(e) => selectAllIncluded(e.target.checked)}
                                            sx={{ color: 'white', '&.Mui-checked': { color: 'white' }, '&.MuiCheckbox-indeterminate': { color: 'white' } }}
                                        />
                                    </StyledTableCell>
                                    <StyledTableCell>Student</StyledTableCell>
                                    <StyledTableCell>Course</StyledTableCell>
                                    <StyledTableCell>Trigger</StyledTableCell>
                                    <StyledTableCell>Progress</StyledTableCell>
                                    <StyledTableCell>Parent Email</StyledTableCell>
                                    <StyledTableCell>Force Fail</StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {candidates.map((c) => {
                                    const key = `${c.enrollment_id}_${c.trigger_type}`;
                                    const sel = selections[key] || { include: false, force_fail: null };
                                    return (
                                        <TableRow key={key} hover>
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    size="small"
                                                    checked={sel.include}
                                                    onChange={() => toggleInclude(key)}
                                                />
                                            </TableCell>
                                            <TableCell>{c.student_name}</TableCell>
                                            <TableCell>{c.course_title}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={TRIGGER_LABEL[c.trigger_type] || c.trigger_type}
                                                    color={TRIGGER_COLOR[c.trigger_type] || 'default'}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>{c.completed_count}/{c.total_sessions}</TableCell>
                                            <TableCell>
                                                {c.has_parent_email ? (
                                                    <Chip label="Yes" size="small" color="success" variant="outlined" />
                                                ) : (
                                                    <Chip label="No" size="small" color="error" variant="outlined" />
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    select
                                                    size="small"
                                                    value={sel.force_fail || 'none'}
                                                    onChange={(e) => setForceFail(key, e.target.value)}
                                                    disabled={!sel.include}
                                                    sx={{ minWidth: 140 }}
                                                >
                                                    <MenuItem value="none">None</MenuItem>
                                                    <MenuItem value="content_gen">content_gen</MenuItem>
                                                    <MenuItem value="email_send">email_send</MenuItem>
                                                </TextField>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>

            {/* Phase 2: Review */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography variant="h6" fontWeight="bold">Phase 2 — Review ({rows.length} dev rows)</Typography>
                    <Box display="flex" gap={1}>
                        <Button
                            variant="outlined"
                            startIcon={<MailOutlineIcon />}
                            onClick={sendPreview}
                            disabled={!sessionActive || busyPhase !== null}
                        >
                            Send Preview
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<OpenInNewIcon />}
                            onClick={() => window.open('/admin/reports?tab=auto&include_dev=1', '_blank', 'noopener')}
                        >
                            Open in Admin Panel
                        </Button>
                        <Button variant="text" onClick={fetchRows} disabled={busyPhase !== null}>
                            Refresh
                        </Button>
                    </Box>
                </Box>
                {rows.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">
                        No dev_test rows yet. Run a scan to create some.
                    </Typography>
                ) : (
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell>ID</StyledTableCell>
                                    <StyledTableCell>Status</StyledTableCell>
                                    <StyledTableCell>Trigger</StyledTableCell>
                                    <StyledTableCell>Student</StyledTableCell>
                                    <StyledTableCell>Course</StyledTableCell>
                                    <StyledTableCell>Retry</StyledTableCell>
                                    <StyledTableCell>Force Fail</StyledTableCell>
                                    <StyledTableCell>Error</StyledTableCell>
                                    <StyledTableCell>Confirmed</StyledTableCell>
                                    <StyledTableCell>Sent</StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map(r => {
                                    const isFlashing = flashRowIds.has(r.id);
                                    return (
                                        <TableRow
                                            key={r.id}
                                            sx={{
                                                backgroundColor: isFlashing
                                                    ? alpha(theme.palette.warning.main, 0.2)
                                                    : 'inherit',
                                                transition: 'background-color 2s',
                                            }}
                                        >
                                            <TableCell>{r.id}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={r.status}
                                                    color={STATUS_COLOR[r.status] || 'default'}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={TRIGGER_LABEL[r.trigger_type] || r.trigger_type}
                                                    color={TRIGGER_COLOR[r.trigger_type] || 'default'}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>{r.student_name}</TableCell>
                                            <TableCell>{r.course_name}</TableCell>
                                            <TableCell>{r.daily_retry_count}/3</TableCell>
                                            <TableCell>
                                                <Tooltip title="Toggle email_send failure injection">
                                                    <Chip
                                                        label={r.force_fail || '—'}
                                                        size="small"
                                                        color={r.force_fail ? 'error' : 'default'}
                                                        variant={r.force_fail ? 'filled' : 'outlined'}
                                                        onClick={() => toggleRowForceFail(r.id, r.force_fail)}
                                                        sx={{ cursor: 'pointer' }}
                                                    />
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell sx={{ maxWidth: 220, fontSize: 11 }}>
                                                {r.error_message ? (
                                                    <Typography variant="caption" color="error.main">
                                                        {r.error_message.slice(0, 80)}
                                                    </Typography>
                                                ) : '—'}
                                            </TableCell>
                                            <TableCell>
                                                {r.confirmed_at
                                                    ? new Date(r.confirmed_at).toLocaleTimeString('en-US', { hour12: false })
                                                    : '—'}
                                            </TableCell>
                                            <TableCell>
                                                {r.completed_at
                                                    ? new Date(r.completed_at).toLocaleTimeString('en-US', { hour12: false })
                                                    : '—'}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>

            {/* Phase 3: Send & follow-up */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" fontWeight="bold" mb={1}>Phase 3 — Send & Follow-up</Typography>
                <Typography variant="body2" color="textSecondary" mb={2}>
                    Confirm rows in the admin panel first (or force-fail them here). Each button below can be
                    clicked multiple times — Run Send + Run Maintenance repeated cycles let you test the 3-day
                    retry cap. Watch the rows flash as their status changes.
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                    <Button
                        variant="contained"
                        color="success"
                        startIcon={<SendIcon />}
                        onClick={() => runSends('all')}
                        disabled={!sessionActive || busyPhase !== null}
                    >
                        Run Send (all)
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<SendIcon />}
                        onClick={() => runSends('midterm')}
                        disabled={!sessionActive || busyPhase !== null}
                    >
                        Send Midterm
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<SendIcon />}
                        onClick={() => runSends('3rem')}
                        disabled={!sessionActive || busyPhase !== null}
                    >
                        Send 3-Rem
                    </Button>
                    <Divider orientation="vertical" flexItem />
                    <Button
                        variant="contained"
                        color="warning"
                        startIcon={<BuildIcon />}
                        onClick={runMaintenance}
                        disabled={!sessionActive || busyPhase !== null}
                    >
                        Run Maintenance
                    </Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<SummarizeIcon />}
                        onClick={runDigest}
                        disabled={!sessionActive || busyPhase !== null}
                    >
                        Run Daily Digest
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
};

export default DevAutoReportTesting;
